import { Controller } from "@hotwired/stimulus"
import { destroy, post } from "@rails/request.js"
import { SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"
import { clearButtonBusy, markButtonBusy } from "../lib/button_loading"

// Step 2 email-edit: reveals an inline edit form, POSTs to /onboarding/update_email, dispatches email-edit:email-changed on success for sibling controllers.
//
// It also owns the way OUT of the wizard (#13665): the app redirects every
// other page back into onboarding, so an agent signed in under the wrong
// account (a mistaken Google pick, an old address) needs "Not you? Sign out"
// and an "already registered → Sign in instead?" link that actually reaches
// the sign-in page — both end the current session first, then navigate.
export default class extends Controller {
  static targets = ["displayMode", "editMode", "addressDisplay", "emailInput", "passwordField", "passwordInput", "saveBtn", "error", "success"]
  static values  = { updateUrl: String, requiresPassword: Boolean, sessionUrl: String, signInUrl: String }

  disconnect() {
    if (this._cancelTimer) clearTimeout(this._cancelTimer)
  }

  // BUTTON-9: "Save & Resend Verification" is enabled only while the typed
  // address differs from the one on screen — compared the way User normalizes
  // email_address (strip + downcase), so a case-only edit is not a change.
  emailInputTargetConnected(input) {
    input.addEventListener("input", this.onEmailInput)
  }

  emailInputTargetDisconnected(input) {
    input.removeEventListener("input", this.onEmailInput)
  }

  onEmailInput = () => this.syncSaveState()

  isDirty() {
    if (!this.hasEmailInputTarget) return false
    return normalizeEmail(this.emailInputTarget.value) !== normalizeEmail(this.currentDisplayedEmail())
  }

  syncSaveState() {
    if (!this.hasSaveBtnTarget) return
    syncGateReason(this.saveBtnTarget, { ready: this.isDirty(), reason: SAVE_UNCHANGED_REASON })
  }

  enterEditMode() {
    // Clear any pending auto-dismiss so it can't close the form the user just reopened.
    if (this._cancelTimer) { clearTimeout(this._cancelTimer); this._cancelTimer = null }
    if (this.hasDisplayModeTarget) this.displayModeTarget.classList.add("d-none")
    if (this.hasEditModeTarget) this.editModeTarget.classList.remove("d-none")
    this.clearMessages()
    if (this.hasEmailInputTarget) {
      this.emailInputTarget.value = this.currentDisplayedEmail()
      this.emailInputTarget.focus()
      this.emailInputTarget.select()
    }
    this.syncSaveState()
  }

  cancel() {
    if (this.hasDisplayModeTarget) this.displayModeTarget.classList.remove("d-none")
    if (this.hasEditModeTarget) this.editModeTarget.classList.add("d-none")
    this.clearMessages()
    if (this.hasEmailInputTarget) this.emailInputTarget.value = ""
    if (this.hasPasswordInputTarget) this.passwordInputTarget.value = ""
  }

  async save() {
    if (!this.hasEmailInputTarget || !this.isDirty()) return
    const newEmail = this.emailInputTarget.value.trim()
    if (!newEmail) { this.showFieldFriction("Please enter an email address."); return }

    this.clearMessages()
    // BUTTON-8: shared spinner + aria-busy, label kept, second click blocked.
    this.disableSave(true)
    if (this.hasSaveBtnTarget) markButtonBusy(this.saveBtnTarget)

    const body = { email_address: newEmail }
    if (this.requiresPasswordValue && this.hasPasswordInputTarget) {
      body.current_password = this.passwordInputTarget.value
    }

    try {
      const response = await post(this.updateUrlValue, { body, responseKind: "json" })
      const data = await response.json
      if (response.ok && data.success) {
        this.handleSuccess(data)
      } else {
        this.handleFailure(data, response.statusCode)
      }
    } catch (err) {
      this.showError("Network error. Please try again.")
      console.error("[email-edit] request failed", err)
    } finally {
      if (this.hasSaveBtnTarget) clearButtonBusy(this.saveBtnTarget)
      this.syncSaveState()
    }
  }

  handleSuccess(data) {
    if (this.hasAddressDisplayTarget && data.email) this.addressDisplayTarget.textContent = data.email
    this.showSuccess(data.message || "Email updated. Check your inbox.")
    // Notify sibling controllers: poller resets its badge + resumes polling, gate re-disables Continue.
    this.dispatch("email-changed")
    // New address is unverified — drop the password requirement, clear and hide the field so a reopened form doesn't demand it.
    this.requiresPasswordValue = false
    if (this.hasPasswordInputTarget) this.passwordInputTarget.value = ""
    if (this.hasPasswordFieldTarget) this.passwordFieldTarget.classList.add("d-none")
    // Auto-dismiss after 1.5s; stored so disconnect() can cancel it.
    this._cancelTimer = setTimeout(() => this.cancel(), 1500)
  }

  handleFailure(data, _statusCode) {
    if (data && data.sign_in_url) {
      this.showErrorWithSignIn(data.message || "Could not update email.", data.sign_in_url)
    } else {
      this.showError(data?.message || "Could not update email. Please try again.")
    }
  }

  currentDisplayedEmail() {
    return this.hasAddressDisplayTarget ? this.addressDisplayTarget.textContent.trim() : ""
  }

  disableSave(yes) {
    if (this.hasSaveBtnTarget) this.saveBtnTarget.disabled = yes
  }

  showError(msg) {
    this.renderError(msg)
    this.announceError(msg)
  }

  // Local blank-field guard: same message on screen, but escalated through the
  // THROTTLED path. Clicking Save with an empty email is ordinary form friction —
  // popping the support prompt on the first click would nag every user before
  // anything has actually failed.
  showFieldFriction(msg) {
    this.renderError(msg)
    this.dispatch("client-error", { detail: { message: msg } })
  }

  renderError(msg) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("d-none")
    if (this.hasSuccessTarget) this.successTarget.classList.add("d-none")
  }

  // Tell the wizard root this step failed so signup-support can offer a support
  // ticket, carrying the exact text the user saw. Event name: email-edit:error.
  announceError(message) {
    this.dispatch("error", { detail: { message } })
  }

  // "Not you? Sign out" — end this session and go to sign-in.
  async signOut(event) {
    event?.preventDefault()
    await this.signOutTo(this.signInUrlValue || "/session/new")
  }

  // Ending the session first is what makes the sign-in link work: while the
  // wizard session stands, /session/new bounces an authenticated user to /leads
  // and the onboarding guard sends them straight back here. A failed sign-out
  // request still navigates — the sign-in page then reports the live session.
  async signOutTo(url) {
    if (this.hasSessionUrlValue && this.sessionUrlValue) {
      try {
        await destroy(this.sessionUrlValue, { responseKind: "html" })
      } catch (err) {
        console.error("[email-edit] sign out failed", err)
      }
    }
    this.navigateTo(url)
  }

  // Wrapped for testability — jsdom's window.location.assign is non-configurable.
  navigateTo(url) {
    window.location.assign(url)
  }

  // DOM construction (not innerHTML) keeps the server-returned URL out of an HTML-parsing context.
  showErrorWithSignIn(message, signInUrl) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = `${message} `
    const link = document.createElement("a")
    link.setAttribute("href", signInUrl)
    link.className = "gl-onboarding-link"
    link.textContent = "Sign in instead?"
    link.addEventListener("click", (event) => {
      event.preventDefault()
      this.signOutTo(signInUrl)
    })
    this.errorTarget.appendChild(link)
    this.errorTarget.classList.remove("d-none")
    if (this.hasSuccessTarget) this.successTarget.classList.add("d-none")
    // Not routed through showError (which would overwrite the sign-in link with
    // plain text), so the escalation signal is emitted explicitly here.
    this.announceError(message)
  }

  showSuccess(msg) {
    if (!this.hasSuccessTarget) return
    this.successTarget.textContent = msg
    this.successTarget.classList.remove("d-none")
    if (this.hasErrorTarget) this.errorTarget.classList.add("d-none")
  }

  clearMessages() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("d-none")
    }
    if (this.hasSuccessTarget) {
      this.successTarget.textContent = ""
      this.successTarget.classList.add("d-none")
    }
  }
}

function normalizeEmail(value) {
  return (value || "").trim().toLowerCase()
}
